import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { Column } from "../Column";

import { STAFFER_PET_SOURCE_SIZE_PX as SOURCE_SIZE_PX } from "@/lib/constants";
import { stafferPetImages } from "@/lib/stafferPetImages";
import { stafferPets } from "@/lib/stafferPets";
import { cn } from "@/lib/utils";

import type {
  StafferPetPaletteId,
  StafferPetSpecies,
  StafferPetTopperOffset,
} from "@/types";

const DEFAULT_SIZE_PX = 96;
// Keep Peter near the bottom without clipping his silhouette in square previews.
const PETER_VERTICAL_OFFSET_RATIO = 0.08;

const TOPPER_PLACEMENT_BY_SPECIES: Record<
  StafferPetSpecies,
  { headAnchorX: number; headAnchorY: number; widthScale: number }
> = {
  FOX: { headAnchorX: 136, headAnchorY: 72, widthScale: 1 },
  DRAGON: { headAnchorX: 149, headAnchorY: 75, widthScale: 0.9 },
  CAT: { headAnchorX: 146, headAnchorY: 72, widthScale: 1 },
  BLUE_JAY: { headAnchorX: 151, headAnchorY: 79, widthScale: 0.85 },
  CARDINAL: { headAnchorX: 154, headAnchorY: 79, widthScale: 0.85 },
  COCKATIEL: { headAnchorX: 168, headAnchorY: 72, widthScale: 0.85 },
  KINGFISHER: { headAnchorX: 135, headAnchorY: 62, widthScale: 0.9 },
  OWL: { headAnchorX: 132, headAnchorY: 56, widthScale: 0.95 },
  CROW: { headAnchorX: 155, headAnchorY: 64, widthScale: 0.85 },
  WREN: { headAnchorX: 143, headAnchorY: 72, widthScale: 0.8 },
  BALD_EAGLE: { headAnchorX: 133, headAnchorY: 29, widthScale: 0.8 },
  DUNE_WORM: { headAnchorX: 181, headAnchorY: 42, widthScale: 0.8 },
  BAT: { headAnchorX: 132, headAnchorY: 79, widthScale: 0.8 },
  PHOENIX: { headAnchorX: 163, headAnchorY: 60, widthScale: 0.8 },
  DIREWOLF: { headAnchorX: 174, headAnchorY: 43, widthScale: 0.9 },
  PETER: { headAnchorX: 151, headAnchorY: 29, widthScale: 0.9 },
};

export const StafferPetSprite = ({
  species,
  paletteId,
  sizePx = DEFAULT_SIZE_PX,
  className,
  isAnimated = false,
  isBouncing = false,
  isTripleBounce = false,
  hasPaletteGlow = true,
  topperId = null,
  topperOffset = { x: 0, y: 0 },
  onTopperOffsetChange,
  accessoryId = null,
  accessoryAdjustment = { x: 0, y: 0, scale: 1 },
  onAccessoryAdjustmentChange,
  accessoryId2 = null,
  accessoryAdjustment2 = { x: 0, y: 0, scale: 1 },
  onAccessoryAdjustmentChange2,
  onAccessoryDragStart,
}: {
  species: StafferPetSpecies;
  paletteId?: StafferPetPaletteId;
  sizePx?: number;
  className?: string;
  isAnimated?: boolean;
  isBouncing?: boolean;
  isTripleBounce?: boolean;
  hasPaletteGlow?: boolean;
  topperId?: string | null;
  topperOffset?: StafferPetTopperOffset;
  onTopperOffsetChange?: (offset: StafferPetTopperOffset) => void;
  accessoryId?: string | null;
  accessoryAdjustment?: StafferPetTopperOffset;
  onAccessoryAdjustmentChange?: (adjustment: StafferPetTopperOffset) => void;
  accessoryId2?: string | null;
  accessoryAdjustment2?: StafferPetTopperOffset;
  onAccessoryAdjustmentChange2?: (adjustment: StafferPetTopperOffset) => void;
  onAccessoryDragStart?: (slot: 1 | 2) => void;
}) => {
  /* React Hooks */
  const topperDragRef = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    offset: StafferPetTopperOffset;
  } | null>(null);
  const accessoryDragRef = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    adjustment: StafferPetTopperOffset;
    slot: 1 | 2;
  } | null>(null);
  const sprite = useMemo(
    () => ({
      palette: stafferPets.getPalette(species, paletteId),
      src: stafferPets.getSpriteSrc(species, paletteId),
    }),
    [paletteId, species],
  );
  const [recoloredSprite, setRecoloredSprite] = useState<{
    sprite: typeof sprite;
    src: string;
  }>();
  const isNaturalPalette = paletteId === stafferPets.NATURAL_PALETTE_ID;

  useEffect(() => {
    if (isNaturalPalette) return;

    const { url, release } = stafferPetImages.retain(
      sprite.src,
      sprite.palette,
      paletteId,
    );
    let isCurrent = true;
    const setCurrentSprite = (src: string) => {
      if (isCurrent) setRecoloredSprite({ sprite, src });
    };
    void url.then(setCurrentSprite, () => {
      setCurrentSprite(sprite.src);
    });

    return () => {
      isCurrent = false;
      release();
    };
  }, [isNaturalPalette, paletteId, sprite]);

  /* Helpers */
  const recoloredSrc =
    recoloredSprite?.sprite === sprite ? recoloredSprite.src : undefined;
  const spriteSrc = isNaturalPalette ? sprite.src : recoloredSrc;
  const { palette } = sprite;
  // Dense lists opt out: blurred glows get expensive across many sprites.
  const hasGlow =
    hasPaletteGlow && paletteId
      ? stafferPets.GLOW_PALETTE_IDS.includes(paletteId)
      : false;
  const topper = stafferPets.getTopper(topperId);
  const accessory = stafferPets.getAccessory(accessoryId);
  const accessory2 =
    accessoryId2 === accessoryId
      ? null
      : stafferPets.getAccessory(accessoryId2);
  const topperPlacement = TOPPER_PLACEMENT_BY_SPECIES[species];
  const topperWidthPercent = topper
    ? topper.widthPercent *
      topperPlacement.widthScale *
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      (topperOffset.scale || 1)
    : 0;
  const topperLeftPercent = topper
    ? (topperPlacement.headAnchorX / SOURCE_SIZE_PX) * 100 -
      (topper.anchorX / SOURCE_SIZE_PX) * topperWidthPercent +
      (topperOffset.x / SOURCE_SIZE_PX) * 100
    : 0;
  const topperTopPercent = topper
    ? (topperPlacement.headAnchorY / SOURCE_SIZE_PX) * 100 -
      (topper.anchorY / SOURCE_SIZE_PX) * topperWidthPercent +
      (topperOffset.y / SOURCE_SIZE_PX) * 100
    : 0;

  /* Handlers */
  const handleTopperPointerDown = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    if (!onTopperOffsetChange) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    topperDragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      offset: topperOffset,
    };
  };

  const handleTopperPointerMove = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    const drag = topperDragRef.current;
    if (
      !onTopperOffsetChange ||
      drag === null ||
      drag.pointerId !== event.pointerId
    ) {
      return;
    }

    const scaleToSource = SOURCE_SIZE_PX / sizePx;
    const x = Math.round(
      drag.offset.x + (event.clientX - drag.clientX) * scaleToSource,
    );
    const y = Math.round(
      drag.offset.y + (event.clientY - drag.clientY) * scaleToSource,
    );
    onTopperOffsetChange(
      stafferPets.clampTopperOffset(
        { x, y, scale: drag.offset.scale },
        topperId,
      ),
    );
  };

  const handleTopperPointerEnd = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    if (topperDragRef.current?.pointerId !== event.pointerId) return;
    topperDragRef.current = null;
  };

  const handleAccessoryPointerDown = (
    event: ReactPointerEvent<HTMLImageElement>,
    slot: 1 | 2,
  ) => {
    if (
      !(slot === 1 ? onAccessoryAdjustmentChange : onAccessoryAdjustmentChange2)
    )
      return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    onAccessoryDragStart?.(slot);
    accessoryDragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      adjustment: slot === 1 ? accessoryAdjustment : accessoryAdjustment2,
      slot,
    };
  };

  const handleAccessoryPointerMove = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    const drag = accessoryDragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) {
      return;
    }
    const onChange =
      drag.slot === 1
        ? onAccessoryAdjustmentChange
        : onAccessoryAdjustmentChange2;
    const draggedAccessoryId = drag.slot === 1 ? accessoryId : accessoryId2;
    if (!onChange) return;

    const scaleToSource = SOURCE_SIZE_PX / sizePx;
    const x = Math.round(
      drag.adjustment.x + (event.clientX - drag.clientX) * scaleToSource,
    );
    const y = Math.round(
      drag.adjustment.y + (event.clientY - drag.clientY) * scaleToSource,
    );
    onChange(
      stafferPets.clampAccessoryAdjustment(
        {
          x,
          y,
          scale: drag.adjustment.scale,
          rotation: drag.adjustment.rotation,
        },
        draggedAccessoryId,
      ),
    );
  };

  const handleAccessoryPointerEnd = (
    event: ReactPointerEvent<HTMLImageElement>,
  ) => {
    if (accessoryDragRef.current?.pointerId !== event.pointerId) return;
    accessoryDragRef.current = null;
  };

  /* Render */
  const renderAccessory = (slot: 1 | 2) => {
    const item = slot === 1 ? accessory : accessory2;
    const adjustment = slot === 1 ? accessoryAdjustment : accessoryAdjustment2;
    const onChange =
      slot === 1 ? onAccessoryAdjustmentChange : onAccessoryAdjustmentChange2;
    if (item === null) return null;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const width = item.widthPercent * (adjustment.scale || 1);
    const left =
      50 -
      width / 2 +
      ((item.defaultX || 0) / SOURCE_SIZE_PX) * 100 +
      (adjustment.x / SOURCE_SIZE_PX) * 100;
    const top =
      50 -
      width / 2 +
      ((item.defaultY || 0) / SOURCE_SIZE_PX) * 100 +
      (adjustment.y / SOURCE_SIZE_PX) * 100;
    const isBehind = "layer" in item && item.layer === "BEHIND";

    return (
      <img
        alt=""
        src={item.src}
        draggable={false}
        onPointerCancel={handleAccessoryPointerEnd}
        onPointerDown={(event) => {
          handleAccessoryPointerDown(event, slot);
        }}
        onPointerMove={handleAccessoryPointerMove}
        onPointerUp={handleAccessoryPointerEnd}
        className={cn(
          "absolute h-auto max-w-none select-none",
          isBehind ? "z-0" : "z-20",
          onChange
            ? "pointer-events-auto cursor-grab touch-none active:cursor-grabbing"
            : "pointer-events-none",
        )}
        style={{
          imageRendering: "pixelated",
          left: `${left.toString()}%`,
          top: `${top.toString()}%`,
          transform: `rotate(${(item.rotation + (adjustment.rotation ?? 0)).toString()}deg)`,
          width: `${width.toString()}%`,
        }}
      />
    );
  };

  const render = () => {
    return (
      <Column
        className={cn(
          "relative h-full w-full",
          isBouncing &&
            (isTripleBounce
              ? "staffer-pet-bounce-triple"
              : "staffer-pet-bounce"),
          !isBouncing && isAnimated && "staffer-pet-float",
          className,
        )}
        style={{
          height: sizePx,
          top:
            species === "PETER"
              ? sizePx * PETER_VERTICAL_OFFSET_RATIO
              : undefined,
          width: sizePx,
        }}
      >
        <img
          alt=""
          src={spriteSrc}
          draggable={false}
          height={SOURCE_SIZE_PX}
          width={SOURCE_SIZE_PX}
          className={cn(
            "pointer-events-none relative z-10 block h-full w-full select-none",
            paletteId === "RAINBOW" &&
              hasPaletteGlow &&
              "staffer-pet-rainbow-shift",
          )}
          style={{
            imageRendering: "pixelated",
            filter:
              hasGlow
                ? `drop-shadow(0 0 1px ${palette.body}) drop-shadow(0 0 3px ${palette.body}) drop-shadow(0 2px 3px rgba(0, 0, 0, 0.4))`
                : undefined,
          }}
        />
        {renderAccessory(1)}
        {renderAccessory(2)}
        {topper ? (
          <img
            alt=""
            src={topper.src}
            draggable={false}
            onPointerCancel={handleTopperPointerEnd}
            onPointerDown={handleTopperPointerDown}
            onPointerMove={handleTopperPointerMove}
            onPointerUp={handleTopperPointerEnd}
            className={cn(
              "absolute z-30 h-auto max-w-none select-none",
              onTopperOffsetChange
                ? "pointer-events-auto cursor-grab touch-none active:cursor-grabbing"
                : "pointer-events-none",
            )}
            style={{
              imageRendering: "pixelated",
              left: `${topperLeftPercent.toString()}%`,
              top: `${topperTopPercent.toString()}%`,
              width: `${topperWidthPercent.toString()}%`,
            }}
          />
        ) : null}
      </Column>
    );
  };

  return render();
};
